import { logData } from "@jb-dotcom/fe-logger";
import { DynamicYieldClient } from "./client";

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- window must be an interface
  interface Window {
    addEventListener: Window["addEventListener"];
    // eslint-disable-next-line @typescript-eslint/no-wrapper-object-types -- types for Next
    dataLayer?: Object[] | undefined;
    DY?: {
      API: (a: string, b: unknown) => void;
      userActiveConsent: {
        accepted: boolean;
      };
      recommendationContext:
        | {
            type: string;
            data?: undefined;
          }
        | {
            type: string;
            data: {
              url: string;
            }[];
          }
        | undefined;
    };
    DYO?: {
      ActiveConsent: {
        updateConsentAcceptedStatus: (argument0: boolean) => void;
      };
    };
    location: Window["location"];
    postMessage: Window["postMessage"];
    PrivacyManagerAPI?:
      | {
          action: string | undefined;
          capabilities: object | undefined;
          callApi: (
            type: string,
            host: string,
          ) => { consentDecision: string[] };
        }
      | undefined;
  }
}
const DynamicYield = ({ sectionId }: { sectionId: string | undefined }) => (
  <>
    {/* PreConnecting to API Urls to improve app performance */}
    <link href="https://cdn.dynamicyield.com" rel="preconnect" />
    <link href="https://st.dynamicyield.com" rel="preconnect" />
    <link href="https://rcom.dynamicyield.com" rel="preconnect" />
    <link href="https://cdn.dynamicyield.com" rel="dns-prefetch" />
    <link href="https://st.dynamicyield.com" rel="dns-prefetch" />
    <link href="https://rcom.dynamicyield.com" rel="dns-prefetch" />
    <DynamicYieldClient sectionId={sectionId} />
  </>
);

DynamicYield.listenForPrivacyManagerApi = () => {
  const privacyManagerApiMaxChecks = 30;
  let privacyManagerApiNumberOfChecks = 0;
  /*
    First step is to register with the CM API to receive callbacks when a preference update occurs. 
    You must wait for the CM API (PrivacyManagerAPI object) to exist on the page before registering.
    */
  const privacyInterval = setInterval(() => {
    if (
      privacyManagerApiNumberOfChecks < privacyManagerApiMaxChecks &&
      // eslint-disable-next-line unicorn/prefer-global-this -- need in order to use window
      Object.hasOwn(window, "PrivacyManagerAPI")
    ) {
      const apiObject = {
        PrivacyManagerAPI: {
          action: "getConsentDecision",
          // eslint-disable-next-line unicorn/prefer-global-this -- need in order to use window
          self: window.location.host,
          timestamp: Date.now(),
        },
      };
      // eslint-disable-next-line unicorn/prefer-global-this -- need in order to use window
      if (!window.location.host.startsWith("localhost")) {
        // eslint-disable-next-line unicorn/prefer-global-this -- need in order to use window
        window.postMessage(JSON.stringify(apiObject), window.location.origin);
      }
      clearInterval(privacyInterval);
    } else {
      privacyManagerApiNumberOfChecks++;
    }
  }, 1000);
};
DynamicYield.init = () => {
  const dyMaxChecks = 30;
  let dyChecks = 0;
  const dyInterval = setInterval(() => {
    if (
      dyChecks < dyMaxChecks &&
      // eslint-disable-next-line unicorn/prefer-global-this -- need in order to use window
      Object.hasOwn(window, "DY")
    ) {
      // eslint-disable-next-line sonarjs/post-message -- origin intentionally unrestricted for TrustArc CM API callbacks
      window.addEventListener("message", (event) => {
        // eslint-disable-next-line unicorn/prefer-global-this -- need in order to use window
        DynamicYield.checkConsent(event, window);
      });
      clearInterval(dyInterval);
      DynamicYield.setPageContext(
        // eslint-disable-next-line unicorn/prefer-global-this -- need in order to use window
        window.location.href,
        // eslint-disable-next-line unicorn/prefer-global-this -- need in order to use window
        window.location.pathname === "/",
      );
      DynamicYield.listenForPrivacyManagerApi();
    } else {
      dyChecks++;
    }
  }, 100);
};

DynamicYield.setPageContext = (currentUrl?: string, isHomePage?: boolean) => {
  // eslint-disable-next-line unicorn/prefer-global-this -- need in order to use window
  if (window.DY) {
    if (isHomePage) {
      // eslint-disable-next-line unicorn/prefer-global-this -- need in order to use window
      window.DY.recommendationContext = { type: "HOMEPAGE" };
    } else if (currentUrl) {
      // eslint-disable-next-line unicorn/prefer-global-this -- need in order to use window
      window.DY.recommendationContext = {
        data: [{ url: currentUrl }],
        type: "OTHER",
      };
    } // We can make this data section anything we want
  }
};

DynamicYield.updateDynamicYieldConsent = (value: boolean) => {
  void logData({ message: "DY consent updated", newValue: value });
  // eslint-disable-next-line unicorn/prefer-global-this -- need in order to use window
  if (window.DY) {
    // eslint-disable-next-line unicorn/prefer-global-this -- need in order to use window
    window.DY.userActiveConsent = {
      accepted: value,
    };
    // eslint-disable-next-line unicorn/prefer-global-this -- need in order to use window
    window.DYO?.ActiveConsent.updateConsentAcceptedStatus(value);
  }
  try {
    localStorage.setItem("userActiveConsent", JSON.stringify(value));
  } catch {
    console.error("We couldn't save the value of the consent,", value);
  }
};

DynamicYield.getCookie = (key: string): string | undefined =>
  document.cookie
    .split("; ")
    .find((cookie) => cookie.startsWith(`${key}=`))
    ?.split("=")
    .slice(1)
    .join("=");

type PrivacyManagerApiMessage = {
  PrivacyManagerAPI?: {
    capabilities?: object;
    action?: string;
  };
};

DynamicYield.dispatchGdprLabels = (labels: string[], win: Window) => {
  const dispatched: Record<string, number> = {};
  for (const label of labels) {
    if (!dispatched[label]) {
      win.dataLayer?.push({ event: "GDPR Pref Allows " + label });
      dispatched[label] = 1;
    }
  }
};

DynamicYield.checkConsent = (event: MessageEvent, win: Window) => {
  /*
   Callbacks will occur in the form of a PostMessage event. This code listens for the appropriately formatted PostMessage event,
   gets the new consent decision, and then pushes the events into the GTM framework. Once the event is submitted, that consent decision
   is marked in the 'dispatched' map so it does not occur more than once.
   */
  try {
    if (!event.data) {
      return;
    }
    const parsed = JSON.parse(event.data as string) as PrivacyManagerApiMessage;
    if (
      !parsed.PrivacyManagerAPI?.capabilities ||
      parsed.PrivacyManagerAPI.action !== "getConsentDecision"
    ) {
      return;
    }

    const newDecision = win.PrivacyManagerAPI?.callApi(
      "getGDPRConsentDecision",
      win.location.host,
    ).consentDecision;

    if (!newDecision) {
      return;
    }

    const cookieValue = DynamicYield.getCookie("notice_gdpr_prefs") ?? "0:";
    DynamicYield.updateDynamicYieldConsent(cookieValue !== "0:");
    DynamicYield.dispatchGdprLabels(newDecision, win);
  } catch {
    /** not a cm api message **/
  }
};

DynamicYield.handleLogin = (tbNumber: string) => {
  // eslint-disable-next-line unicorn/prefer-global-this -- need in order to use window
  window.DY?.API("event", {
    name: "Login",
    properties: {
      cuid: tbNumber,
      cuidType: "TrueBlueNumber",
      dyType: "login-v1",
    },
  });
};

export default DynamicYield;
